import React, {CSSProperties, MouseEventHandler, PropsWithChildren, useRef} from 'react';

import {
    StoryFlow,
    MemoriesStorySummary,
    MemoryEconomyStats,
    TotalMemoryEconomy
} from '../economics/AnalyseStoryFlowsEconomyManager';

type MinMaxPointsPropsSingleStory = {
    episodeData: TotalMemoryEconomy;
    affectionPointsList: string[];
}
type MinMaxPointsPropsSummary = {
    memoriesStorySummary: MemoriesStorySummary;
    affectionPointsList: string[];
}
type MinMaxPointsProps = {
    memoriesEconomyStats: Record<number, TotalMemoryEconomy>;
    memoriesStorySummary: MemoriesStorySummary;
    affectionPointsList: string[];
}

const cellStyle: CSSProperties = {
    border: '1px solid gray',
    padding: '4px',
    textAlign: 'center',
};

const Cell = ({children, header, ...rest}: PropsWithChildren & { header?: boolean }) => (
    header ? (
        <th style={{...cellStyle, textAlign: 'left'}} {...rest}>{children}</th>
    ) : (
        <td style={cellStyle} {...rest}>{children}</td>
    )
);

const PropertyRow = ({title, property, memoryNames, data, affectionPointsList}: {
    title: string,
    property: keyof MemoryEconomyStats,
    memoryNames: string[],
    data: MemoriesStorySummary,
    affectionPointsList: string[],
}) => (
    <tr>
        <Cell header>{title}</Cell>
        {affectionPointsList.map((memory) => (
            <Cell
                key={memory}
            >
                {
                    memoryNames.includes(memory) ? (
                        data[memory][property] === 1000000 ? 'N/A' : data[memory][property]
                    ) : ''
                }
            </Cell>
        ))}
    </tr>
);

const propToField: [string, keyof MemoryEconomyStats][] = [
    ['MAX FREE POINTS', 'freeModeMaxPoints'],
    ['MAX PREMIUM POINTS', 'paidModeMaxPoints'],
    ['MIN POINTS', 'minPoints'],

    ['MIN - DECREASE GRANTS', 'minDecreaseGrants'],
    ['MIN - DECREASE POINTS', 'minDecreasePoints'],

    ['MAX FREE - TOTAL GRANTS', 'freeModeMaxGrants'],
    ['MAX PREMIUM - TOTAL GRANTS', 'paidModeMaxGrants'],
    ['MIN - TOTAL GRANTS', 'minTotalGrants'],

    ['MAX PREMIUM - FREE GRANTS', 'paidModeMaxFreeGrants'],
    ['MIN - FREE GRANTS', 'freeModeMinGrants'],

    ['MAX PREMIUM - PREMIUM GRANTS', 'paidModeMaxPaidGrants'],
    ['MAX PREMIUM - FREE NESTED GRANTS', 'paidModeMaxFreeNestedGrants'],

    ['MAX FREE ROUTE - CHECKS', 'maxFreeModeMaxChecks'],
    ['MAX PREMIUM ROUTE - CHECKS', 'paidModeMaxChecks'],

    ['MIN CHECK POINT AMOUNT', 'minChecks'],
    ['MAX CHECK POINT AMOUNT', 'maxChecks'],
];

const MinMaxPointsSingleStory = (props: MinMaxPointsPropsSingleStory) => {
    const {stats} = props.episodeData;
    const {affectionPointsList} = props;
    const memoryNames = Object.keys(stats).sort();

    return (
        <div>
            <table style={{
                borderCollapse: 'collapse',
                border: '1px solid black',
            }}>
                <thead>
                <tr>
                    <Cell header>&nbsp;</Cell>
                    {affectionPointsList.map((memory) => (
                        <Cell header key={memory}>{memory}</Cell>
                    ))}
                </tr>
                </thead>
                <tbody>
                {propToField.map(([title, property]) => (
                    <PropertyRow
                        key={title}
                        title={title}
                        property={property}
                        memoryNames={memoryNames}
                        affectionPointsList={affectionPointsList}
                        data={stats}
                    />
                ))}
                </tbody>
            </table>
        </div>
    );
};

const MinMaxPointsSummary = ({memoriesStorySummary, affectionPointsList}: MinMaxPointsPropsSummary) => {
    const memoryNames = Object.keys(memoriesStorySummary).sort();
    const existingProps = new Set(
        Object.values(memoriesStorySummary)
            .map(p => Object.keys(p))
            .flat()
    );
    const showOnlyProps = propToField.filter(([_, property]) => existingProps.has(property));

    return (
        <div>
            <table style={{
                borderCollapse: 'collapse',
                border: '1px solid black',
            }}>
                <thead>
                <tr>
                    <Cell header>&nbsp;</Cell>
                    {affectionPointsList.map((memory) => (
                        <Cell header key={memory}>{memory}</Cell>
                    ))}
                </tr>
                </thead>
                <tbody>
                {showOnlyProps.map(([title, property]) => (
                    <PropertyRow
                        key={title}
                        title={title}
                        property={property}
                        memoryNames={memoryNames}
                        affectionPointsList={affectionPointsList}
                        data={memoriesStorySummary}
                    />
                ))}
                </tbody>
            </table>
        </div>
    );
};

export const MinMaxPoints = ({memoriesEconomyStats, memoriesStorySummary, affectionPointsList}: MinMaxPointsProps) => {
    const sortedMemories = [...affectionPointsList].sort();
    const rootRef = useRef<HTMLDivElement | null>(null);

    const handleSelectAll = () => {
        if (!rootRef.current) {
            return;
        }

        const range = document.createRange();
        range.selectNodeContents(rootRef.current);

        document.getSelection()?.removeAllRanges();
        document.getSelection()?.addRange(range);
    };

    return (
        <div>
            <button onClick={handleSelectAll}>select all</button>
            <div ref={rootRef}>
                {Object.entries(memoriesEconomyStats).map(([chapter, chapterData]) => (
                    <div key={chapter}>
                        <div style={{fontWeight: 'bold', fontSize: '1.5em'}}>Episode {chapter}</div>
                        <MinMaxPointsSingleStory
                            episodeData={chapterData}
                            affectionPointsList={sortedMemories}
                        />
                    </div>
                ))}
                <h4>Story summary</h4>
                <MinMaxPointsSummary
                    memoriesStorySummary={memoriesStorySummary}
                    affectionPointsList={sortedMemories}
                />
            </div>
        </div>
    );
};
